import {
  getFloorAnalysis,
  normalizeFloorAnalysisResponse,
  type FloorAnalysisResponse,
} from '@/services/projections'
import type { FloorAnalysisWindowMonths } from './FloorAnalysisPanel'

const TTL_MS = 24 * 60 * 60 * 1000
const CACHE_VERSION = 2

type CacheEntry = { version: number; data: FloorAnalysisResponse; fetchedAt: number }

const cache = new Map<FloorAnalysisWindowMonths, CacheEntry>()
const inflight = new Map<FloorAnalysisWindowMonths, Promise<FloorAnalysisResponse>>()

function isStaleExcludedDistributionShape(data: FloorAnalysisResponse): boolean {
  return data.excluded_distribution_count > 0 && data.excluded_distributions === undefined
}

export function getCachedFloorAnalysis(
  windowMonths: FloorAnalysisWindowMonths,
): FloorAnalysisResponse | null {
  const entry = cache.get(windowMonths)
  if (!entry) return null
  if (entry.version !== CACHE_VERSION) {
    cache.delete(windowMonths)
    return null
  }
  if (Date.now() - entry.fetchedAt >= TTL_MS) {
    cache.delete(windowMonths)
    return null
  }
  if (isStaleExcludedDistributionShape(entry.data)) {
    cache.delete(windowMonths)
    return null
  }
  return normalizeFloorAnalysisResponse(entry.data)
}

export function setCachedFloorAnalysis(
  windowMonths: FloorAnalysisWindowMonths,
  data: FloorAnalysisResponse,
): void {
  cache.set(windowMonths, {
    version: CACHE_VERSION,
    data: normalizeFloorAnalysisResponse(data),
    fetchedAt: Date.now(),
  })
}

export function invalidateFloorAnalysisCache(): void {
  cache.clear()
  inflight.clear()
}

export function loadFloorAnalysis(
  windowMonths: FloorAnalysisWindowMonths,
): Promise<FloorAnalysisResponse> {
  const cached = getCachedFloorAnalysis(windowMonths)
  if (cached) return Promise.resolve(cached)
  const existing = inflight.get(windowMonths)
  if (existing) return existing
  const pending = getFloorAnalysis(windowMonths)
    .then((data) => {
      setCachedFloorAnalysis(windowMonths, data)
      return getCachedFloorAnalysis(windowMonths) ?? normalizeFloorAnalysisResponse(data)
    })
    .finally(() => {
      inflight.delete(windowMonths)
    })
  inflight.set(windowMonths, pending)
  return pending
}
